import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Card } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faTrash, faFilter, faSync, faUserTie } from '@fortawesome/free-solid-svg-icons';
import RequireRoles from '../../../../components/RequireRoles';
import PaginatedItems from '../../../../components/administracao/Pagination';
import useFetch from '../../../../hooks/useFetch';
import Swal from 'sweetalert2';

export default function GerenciarAutores() {
    const { params } = useParams();
    const { get, del } = useFetch();

    const defaultState = {
        autores: [],
        count: 0,
        filtros: { nome: "", nacionalidade: "" },
        filtroAtivo: false,
        mostrarModal: false,
        registroAExcluir: { nome: "" },
        loading: true
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [autores, count] = await buscarAutores({});
            setState(s => ({ ...s, autores, count, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarAutores(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") query = query.concat(key, "=", filtros[key], "&");
            }
        }
        try {
            let response = await get('/autorfotografo' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarAutores():', error);
            return [[], 0];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [autores, count] = await buscarAutores(filtros);
        setState({ ...state, autores, count, loading: false });
    }

    const filtrar = async () => {
        let [autores, count] = await buscarAutores(state.filtros);
        setState(s => ({ ...s, autores, count, filtroAtivo: true, loading: false }));
    };

    const limpar = async () => {
        let [autores, count] = await buscarAutores({});
        setState(s => ({ ...s, filtros: defaultState.filtros, autores, count, filtroAtivo: false, loading: false }));
    };

    async function excluirAutor() {
        try {
            let response = await del('/autorfotografo/' + state.registroAExcluir.id_autor);
            if (response.status === 200) {
                let [autores, count] = await buscarAutores(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, autores, count, mostrarModal: false, registroAExcluir: {}, loading: false });
            } else if (response.status === 409) {
                setState({ ...state, mostrarModal: false });
                Swal.fire({ title: "Não foi possível excluir", text: response.result?.message ?? "Autor em uso por alguma fotografia.", icon: "error", confirmButtonColor: "#587b7f" });
            } else {
                console.error('Erro no excluirAutor()');
                Swal.fire({ title: "Não foi possível excluir o autor", text: "Erro inesperado, tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirAutor():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Autores e Fotógrafos</h2>
                    <p className="admin-subtitle">Gerencie os criadores das obras do acervo iconográfico</p>
                </div>
                <Link to="/admin/iconografico/autores/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Autor
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={(e) => { e.preventDefault(); filtrar(); }}>
                        <div className="row g-3">
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Nome do Autor</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Marc Ferrez..."
                                    value={state.filtros.nome}
                                    onChange={e => setState({ ...state, filtros: { ...state.filtros, nome: e.target.value } })}
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label className="label-admin">Nacionalidade</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Brasileira..."
                                    value={state.filtros.nacionalidade}
                                    onChange={e => setState({ ...state, filtros: { ...state.filtros, nacionalidade: e.target.value } })}
                                />
                            </Form.Group>
                        </div>
                        <div className="filter-actions-row mt-3">
                            <div className="d-flex gap-2">
                                <Button type="button" className="btn-apply-filters" onClick={filtrar}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                                </Button>
                                <Button type="button" className="btn-clear-filters" onClick={limpar}>
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Nacionalidade</th>
                                <th>Nascimento</th>
                                <th>Falecimento</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.autores.length > 0 ? (
                                state.autores.map((autor) => (
                                    <tr key={autor.id_autor}>
                                        <td data-label="Nome" className="fw-bold">
                                            <FontAwesomeIcon icon={faUserTie} className="me-2 text-muted small" />
                                            {autor.nome}
                                        </td>
                                        <td data-label="Nacionalidade">{autor.nacionalidade || '-'}</td>
                                        <td data-label="Nascimento">{autor.data_nascimento || '-'}</td>
                                        <td data-label="Falecimento">{autor.data_falecimento || '-'}</td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/iconografico/autores/${autor.id_autor}`} state={{ acao: "Visualizar" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/iconografico/autores/${autor.id_autor}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: autor })}><FontAwesomeIcon icon={faTrash} /></button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-4">Nenhum autor encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.autores.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Autor</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir o autor <strong>{state.registroAExcluir.nome}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" className="btn-modal-cancel" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirAutor}>Confirmar Exclusão</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
